import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import axios from 'axios';
import PropertyRequestReview from './PropertyRequestReview';
import BookingRequestReview from './BookingRequestReview';
import EditRequestReview from './EditRequestReview';
import CancelRequestReview from './CancelRequestReview';
import RegistrationReview from './RegistrationReview';
import BillingProfileRequestReview from './BillingProfileRequestReview';
import { useSocket } from '../../SharedView/contexts/SocketContext';
import RequestRow, { timeAgo, requestEntity } from './RequestRow';
import './pendingClientRequests.css';

// Home-page panel: pending client requests queue for admin/manager/supervisor.
// Renders nothing while the feature flag is off (backend returns 503).
function PendingClientRequests() {
    const [requests, setRequests] = useState([]);
    // Client-uploaded compliance documents awaiting admin verification (the owner
    // 2026-08-07) — a queue item like any other, not just a bell.
    const [unverifiedDocs, setUnverifiedDocs] = useState([]);
    const [docBusy, setDocBusy] = useState(null);
    const [deposits, setDeposits] = useState([]);
    const [enabled, setEnabled] = useState(true);
    const [loading, setLoading] = useState(true);
    const [reviewing, setReviewing] = useState(null);
    const [banner, setBanner] = useState('');
    const navigate = useNavigate();

    const fetchRequests = useCallback(async () => {
        // Requests and pending deposits are two independent streams — fetch both
        // and let one fail without blanking the other.
        const [reqRes, depRes, docsRes] = await Promise.allSettled([
            axios.get('/api/client-change-requests', { params: { status: 'pending' } }),
            axios.get('/api/wallet/deposits/pending'),
            axios.get('/api/clients/documents/unverified'),
        ]);

        if (reqRes.status === 'fulfilled') {
            setRequests(Array.isArray(reqRes.value.data?.requests) ? reqRes.value.data.requests : []);
            setEnabled(true);
        } else if (reqRes.reason?.response?.status === 503) {
            setEnabled(false); // feature flag off — hide the panel entirely
        } else {
            console.error('Error fetching client requests:', reqRes.reason);
        }

        if (depRes.status === 'fulfilled') {
            setDeposits(Array.isArray(depRes.value.data) ? depRes.value.data : []);
        } else {
            console.error('Error fetching pending deposits:', depRes.reason);
        }
        if (docsRes?.status === 'fulfilled') {
            setUnverifiedDocs(Array.isArray(docsRes.value.data) ? docsRes.value.data : []);
        } else if (docsRes) {
            console.error('Error fetching unverified documents:', docsRes.reason);
        }

        setLoading(false);
    }, []);

    useEffect(() => {
        fetchRequests();
    }, [fetchRequests]);

    // Live refresh: every client submission (requests + deposits) already emits a
    // 'new-notification' socket event to admin/manager/supervisor — refetch the
    // queue so new rows appear without a page reload.
    const socket = useSocket();
    useEffect(() => {
        if (!socket) return undefined;
        const onNotification = () => fetchRequests();
        socket.on('new-notification', onNotification);
        return () => socket.off('new-notification', onNotification);
    }, [socket, fetchRequests]);

    const verifyDoc = async (doc) => {
        setDocBusy(doc.id);
        try {
            await axios.post(`/api/clients/${doc.client_id}/documents/${doc.id}/verify`);
            setUnverifiedDocs((prev) => prev.filter((d) => d.id !== doc.id));
        } catch (e) { window.alert('Could not verify the document.'); }
        setDocBusy(null);
    };
    const rejectDoc = async (doc) => {
        const note = window.prompt('Why is this document being rejected? (shown to the client)', '');
        if (note === null) return;
        setDocBusy(doc.id);
        try {
            await axios.post(`/api/clients/${doc.client_id}/documents/${doc.id}/reject`, { note });
            setUnverifiedDocs((prev) => prev.filter((d) => d.id !== doc.id));
        } catch (e) { window.alert('Could not reject the document.'); }
        setDocBusy(null);
    };

    // Where an APPROVED request should send the reviewer — the entity it created
    // or changed. Bookings (create/edit/cancel) → the booking; properties → the
    // property. Profile/task edits have no single detail page, so they stay put.
    const approvedDestination = (type, resultId) => {
        if (!resultId) return null;
        if (['create_booking', 'edit_booking', 'cancel_booking'].includes(type)) return `/bookings/${resultId}`;
        if (['create_property', 'edit_property'].includes(type)) return `/properties/${resultId}`;
        return null;
    };
    const approvedDefaultMessage = (type) => ({
        create_booking: 'Booking created.',
        edit_booking: 'Booking change applied.',
        cancel_booking: 'Cancellation applied.',
        create_property: 'Property created.',
        edit_property: 'Property change applied.',
        edit_client_profile: 'Profile change applied.',
        edit_client_tasks: 'Tasks updated.',
        add_billing_profile: 'Billing entity created.',
        edit_billing_profile: 'Billing entity updated.',
    }[type] || 'Request approved.');

    const handleDone = (outcome, message, resultId) => {
        const type = reviewing?.request_type;
        setReviewing(null);
        fetchRequests();
        if (outcome === 'approved') {
            // Toast (survives the redirect) + jump straight to the created/updated
            // entity so the reviewer sees the result immediately (2026-07-20).
            toast.success(message || approvedDefaultMessage(type));
            const dest = approvedDestination(type, resultId);
            if (dest) navigate(dest);
            return;
        }
        setBanner(message || 'Request rejected.');
        setTimeout(() => setBanner(''), 8000);
    };

    // Merge requests + deposits into one FIFO list (oldest first) so the queue is
    // in true chronological order, instead of "all requests, then all deposits".
    const items = useMemo(() => {
        const merged = [
            ...requests.map((r) => ({ kind: 'request', created_at: r.created_at, key: `req-${r.id}`, data: r })),
            ...deposits.map((d) => ({ kind: 'deposit', created_at: d.created_at, key: `dep-${d.id}`, data: d })),
        ];
        return merged.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    }, [requests, deposits]);

    // Feature off (rare kill-switch) → render nothing. Otherwise the panel
    // renders immediately WITH the page and its list fills in when the fetch
    // resolves — instead of popping in a beat after everything else.
    if (!enabled) {
        return banner ? <div className="pcr-banner">{banner}</div> : null;
    }

    // Nothing pending → no panel at all (2026-08-04): an empty box above
    // the notifications just pushed them down. While loading we also render
    // nothing rather than a placeholder — when something IS pending the panel
    // appears with its list in one paint, and when nothing is, the page never
    // jumps. The banner still shows on its own if an action just resolved.
    if (loading || (requests.length === 0 && deposits.length === 0)) {
        return banner ? <div className="pcr-banner">{banner}</div> : null;
    }

    return (
        <div className="pcr-panel">
            <div className="pcr-panel-header">
                <h3>Pending Client Requests</h3>
                {!loading && <span className="pcr-count">{requests.length + deposits.length}</span>}
            </div>

            {!!banner && <div className="pcr-banner">{banner}</div>}

            {loading ? (
                <div className="pcr-empty">Loading requests…</div>
            ) : requests.length === 0 && deposits.length === 0 && unverifiedDocs.length === 0 ? (
                <div className="pcr-empty">No pending client requests right now.</div>
            ) : null}

            {!loading && unverifiedDocs.length > 0 && (
                <div className="pcr-docverify">
                    <div className="pcr-docverify__head">
                        Documents to verify
                        <span className="pcr-count">{unverifiedDocs.length}</span>
                    </div>
                    {unverifiedDocs.map((d) => (
                        <div className="pcr-docverify__row" key={d.id}>
                            <div className="pcr-docverify__main">
                                <span className="pcr-docverify__client">{d.client_name || `Client #${d.client_id}`}</span>
                                <span className="pcr-docverify__type">
                                    {d.type === 'vat_certificate' ? 'VAT certificate' : 'Trade license'}
                                </span>
                                <span className="pcr-docverify__date">
                                    {d.uploaded_at ? new Date(d.uploaded_at).toLocaleDateString('en-GB') : ''}
                                </span>
                            </div>
                            <div className="pcr-docverify__actions">
                                <a className="pcr-docverify__view" href={d.file_url} target="_blank" rel="noopener noreferrer">View</a>
                                <button type="button" className="pcr-docverify__ok" disabled={docBusy === d.id} onClick={() => verifyDoc(d)}>Verify</button>
                                <button type="button" className="pcr-docverify__no" disabled={docBusy === d.id} onClick={() => rejectDoc(d)}>Reject</button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <div className="pcr-list">
                {items.map((item) => {
                    if (item.kind === 'request') {
                        const request = item.data;
                        // Every row here is pending → the whole row opens the
                        // review modal. The entity chip (Booking/Property/Client)
                        // shows what it targets when it already exists (edits).
                        const ent = requestEntity(request);
                        return (
                            <RequestRow
                                key={item.key}
                                kind="request"
                                data={request}
                                entityRef={ent?.label}
                                metaText={`Requested by ${request.requested_by_first_name} ${request.requested_by_last_name} · ${timeAgo(request.created_at)}`}
                                onRowClick={() => setReviewing(request)}
                            />
                        );
                    }
                    const dep = item.data;
                    return (
                        <RequestRow
                            key={item.key}
                            kind="deposit"
                            data={dep}
                            metaText={`Submitted ${timeAgo(dep.created_at)}`}
                            onRowClick={() => navigate('/payments')}
                        />
                    );
                })}
            </div>

            {reviewing && reviewing.request_type === 'create_property' && (
                <PropertyRequestReview
                    request={reviewing}
                    onClose={() => setReviewing(null)}
                    onDone={handleDone}
                />
            )}
            {reviewing && reviewing.request_type === 'create_booking' && (
                <BookingRequestReview
                    request={reviewing}
                    onClose={() => setReviewing(null)}
                    onDone={handleDone}
                />
            )}
            {reviewing && (reviewing.request_type === 'edit_booking'
                || reviewing.request_type === 'edit_property'
                || reviewing.request_type === 'edit_client_profile'
                || reviewing.request_type === 'edit_client_tasks') && (
                <EditRequestReview
                    request={reviewing}
                    onClose={() => setReviewing(null)}
                    onDone={handleDone}
                />
            )}
            {reviewing && reviewing.request_type === 'cancel_booking' && (
                <CancelRequestReview
                    request={reviewing}
                    onClose={() => setReviewing(null)}
                    onDone={handleDone}
                />
            )}
            {reviewing && reviewing.request_type === 'register_client' && (
                <RegistrationReview
                    request={reviewing}
                    onClose={() => setReviewing(null)}
                    onDone={handleDone}
                />
            )}
            {reviewing && (reviewing.request_type === 'add_billing_profile'
                || reviewing.request_type === 'edit_billing_profile') && (
                <BillingProfileRequestReview
                    request={reviewing}
                    onClose={() => setReviewing(null)}
                    onDone={handleDone}
                />
            )}
        </div>
    );
}

export default PendingClientRequests;
